import React, { useState, useEffect, useContext, createContext } from 'react'
import { actionUpdateReport } from '../../actions/updateReports'
import { ReportContext } from '../../../src/app'
import type Report from '../../common/types/api/report'
import WidgetWrapper from '../../Form/components/Content/WidgetWrapper'
import PdfContent from '../../Form/components/Content/pdf/PdfContent'
import SpreadsheetGenerator from '../../Form/components/SpreadsheetGenerator/index'
import BarSettingsReport from '../../Form/components/Header/BarSettingsReport'
import {
    EditModeContent,
    ReportContentCtx,
    decoratorEditModeContent,
    strToEditModeContent
} from '../context/ReportContentCtx'
import i18n from 'live/services/i18n'
import ContentGeneralSpan, { ContentGeneralSpanIcon } from '../../common/components/ContentGeneralSpan'
import styled from 'styled-components'

interface Props {
    match: {
        params: {
            uid: string
        }
    }
    history: any
    location: any
}

export default function Content({ match, history, location, ...p }: Props): JSX.Element {
    const { report, dispatch } = useContext(ReportContext)
    const [stateReport, setStateReport] = useState({
        loading: true,
        startLoading: false,
        msg: null
    })

    const [editModeContent, setEditModeContent] = useState<EditModeContent>(EditModeContent.PREVIEW)

    const [showModalGlobalSpan, setShowModalGlobalSpan] = useState(false)

    const uid = match?.params?.uid

    useEffect(() => {
        const reportUid = report?.uid

        const checkUpdateReport = (): boolean => {
            if (stateReport.startLoading) {
                return false
            } else {
                return stateReport.loading
            }
        }

        if (checkUpdateReport()) {
            setStateReport({
                loading: true,
                msg: null,
                startLoading: true
            })

            if (reportUid) {
                dispatch({ report: undefined })
            }

            actionUpdateReport(match.params.uid)
                .then(report => dispatch({ report }))
                .then(() => setStateReport({ loading: false, msg: 'success', startLoading: false }))
                .catch((error: Error) => {
                    console.error(error)
                    setStateReport({ loading: false, msg: error.message, startLoading: false })
                })
        }
    }, [match, report, stateReport, stateReport.loading, uid, stateReport.startLoading, dispatch])

    if (stateReport.loading) return <div>loading...</div>
    if (stateReport.msg !== 'success') return <div>{stateReport.msg}</div>

    const setReport = (report: Report): void => dispatch({ report })
    const eventOnHide = (history, urlSearch) => {
        history.push({
            search: `?${new URLSearchParams({
                editModeContent: String(EditModeContent.PREVIEW)
            })}`
        })
        setEditModeContent(EditModeContent.PREVIEW)
    }
    const eventOnOpen = (currentEditModeContent: EditModeContent) => {
        return (history, urlSearch) => {
            history.push({
                search: `?${new URLSearchParams({
                    editModeContent: String(currentEditModeContent)
                })}`
            })
            setEditModeContent(currentEditModeContent)
        }
    }

    const eventOnLoadEditMode = (currentEditModeContent: EditModeContent) => {
        return (history, urlSearch): boolean => {
            const urlMode = strToEditModeContent(urlSearch['editModeContent'])

            if (String(currentEditModeContent) == String(urlMode)) {
                setEditModeContent(currentEditModeContent)
                return true
            }
            return false
        }
    }

    return (
        <div key={uid}>
            <ReportContentCtx.Provider
                value={{ editModeContent, setEditModeContent: decoratorEditModeContent(history) }}
            >
                <BarSettingsReport pageName="content" uid={uid} />
                <ReportContetSubtitleRow className='reportContentSubtitleRow'>
                    <div className='reportContentSubtitleRow__labelContainer'>
                        <span>{i18n("Content Configuration")}</span>
                    </div>
                    <div className='reportContentSubtitleRow__globalSpanConfig'>
                        <ContentGeneralSpanIcon
                            onClick={() => setShowModalGlobalSpan(true)}
                        />
                        {showModalGlobalSpan && <ContentGeneralSpan form onHide={() => setShowModalGlobalSpan(false)}/>}
                    </div>
                </ReportContetSubtitleRow>
                <WidgetWrapper
                    routerMethods={{
                        eventOnLoadEditMode: eventOnLoadEditMode(EditModeContent.BODY),
                        eventOnOpen: eventOnOpen(EditModeContent.BODY),
                        eventOnHide
                    }}
                    report={report}
                    useRouter={true}
                    onChange={setReport}
                ></WidgetWrapper>
                <PdfContent
                    useRouter={true}
                    report={report}
                    onChange={setReport}
                    routerMethods={{
                        eventOnLoadEditMode: eventOnLoadEditMode(EditModeContent.PDF),
                        eventOnOpen: eventOnOpen(EditModeContent.PDF),
                        eventOnHide
                    }}
                ></PdfContent>
                <SpreadsheetGenerator
                    report={report}
                    onChange={setReport}
                    useRouter={true}
                    routerMethods={{
                        eventOnLoadEditMode: eventOnLoadEditMode(EditModeContent.SPREADSHEET),
                        eventOnOpen: eventOnOpen(EditModeContent.SPREADSHEET),
                        eventOnHide
                    }}
                />
            </ReportContentCtx.Provider>
        </div>
    )
}

const ReportContetSubtitleRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 24px;
    margin-bottom: 16px;
    .reportContentSubtitleRow {
        &__labelContainer {
            padding-left: 5px;
            color: ${({ theme }) => theme['gray-800']};
            font-family: ${({ theme }) => theme['font-semibold']};
            font-size: 16px;
            font-style: normal;
            font-weight: 600;
            line-height: 16px; /* 100% */
        }
        &__globalSpanConfig {
        }
    }
`
